import { IMPORT_FIELDS, type AssetKind, type ImportField } from '@gk/contracts'

/** Helping the user map their columns (everything here is a SUGGESTION the user confirms; the server normalises again). */
export type Mapping = Partial<Record<ImportField, string>>

export const normHeader = (h: string): string => h.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

const SYNONYMS: Record<ImportField, string[]> = {
  readable_tag: ['code', 'tag', 'numero', 'no', 'n', 'id', 'identifiant', 'immat', 'immatriculation', 'matricule', 'reference', 'ref', 'repere', 'code equipement', 'n equipement', 'asset', 'asset id', 'asset tag', 'inventaire', 'n inventaire'],
  kind: ['type', 'categorie', 'category', 'genre', 'nature', 'famille', 'kind'],
  asset_type_code: ['sous type', 'sous categorie', 'subtype', 'designation type', 'usage'],
  make: ['marque', 'make', 'constructeur', 'fabricant', 'brand', 'manufacturer'],
  model: ['modele', 'model', 'reference modele', 'type machine', 'designation'],
  serial_no: ['serie', 'n serie', 'numero de serie', 'no de serie', 'serial', 'serial number', 'serial no', 'sn', 'vin', 'chassis', 'n chassis'],
  year: ['annee', 'year', 'millesime', 'annee de fabrication', 'annee modele', 'mise en service'],
  acquired_on: ['acquisition', 'date acquisition', 'date d acquisition', 'date achat', 'date d achat', 'achat', 'acquired', 'purchase date', 'date mise en service'],
  current_hours: ['heures', 'hours', 'compteur', 'heures moteur', 'h moteur', 'hrs', 'engine hours', 'compteur horaire', 'heures compteur'],
}

/**
 * For each target field: the first synonym (they are listed best-first) that equals an unused header, then the first that a
 * header CONTAINS ("Heures moteur" contains "heures"). A header is never matched because it is a fragment of a synonym.
 * Each header is used once, and the most specific fields go first.
 */
export function suggestMapping(headers: string[]): Mapping {
  const norm = headers.map(normHeader)
  const used = new Set<number>()
  const out: Mapping = {}
  const order: ImportField[] = ['serial_no', 'current_hours', 'acquired_on', 'year', 'make', 'model', 'asset_type_code', 'kind', 'readable_tag']
  for (const field of order) {
    const syn = SYNONYMS[field]
    let idx = -1
    for (const s of syn) { idx = norm.findIndex((h, i) => !used.has(i) && h === s); if (idx >= 0) break }
    if (idx < 0) for (const s of syn) { if (s.length <= 3) continue; idx = norm.findIndex((h, i) => !used.has(i) && h.includes(s)); if (idx >= 0) break }
    if (idx >= 0) { used.add(idx); out[field] = headers[idx]! }
  }
  return out
}
export const orderedFields = (): readonly ImportField[] => IMPORT_FIELDS

// ---- kinds: the file's own words -> our six kinds (suggested; the user edits) ------------------------------------------
const KIND_WORDS: Array<[AssetKind, RegExp]> = [
  ['attachment', /accessoire|attachment|remorque|trailer|rouleau|roller|herse|semoir|epandeur|spreader|sprayer|pulverisateur/],
  ['component', /composant|component|unite de coupe|cutting unit|groupe de coupe|moteur|batterie/],
  ['vehicle', /vehicule|vehicle|voiture|camion|utilitaire|gator|kubota rtv|pick ?up|buggy|navette|fourgon|tracteur agricole/],
  ['tool', /outil|tool|tronconneuse|souffleur|debroussailleuse|taille|perceuse|meuleuse|compresseur|pompe/],
  ['fixed', /fixe|fixed|batiment|local|cuve|forage|station|abri/],
  ['machine', /tondeuse|mower|tracteur|tractor|machine|aerateur|aerator|scarificateur|verticut|top ?dress|sableuse|engin|greens|fairway|rough/],
]
export function suggestKind(raw: string): AssetKind | undefined {
  const n = normHeader(raw)
  if (!n) return undefined
  if (['machine', 'vehicle', 'attachment', 'component', 'tool', 'fixed'].includes(n)) return n as AssetKind
  return KIND_WORDS.find(([, re]) => re.test(n))?.[0]
}
/** Distinct values of the kind column, each with a suggestion; keys are lower-cased and trimmed, as the server expects. */
export function kindValues(rows: string[][], headers: string[], header: string | undefined): Array<{ value: string; count: number; suggested?: AssetKind }> {
  if (!header) return []
  const i = headers.indexOf(header)
  if (i < 0) return []
  const counts = new Map<string, number>()
  for (const r of rows) { const v = (r[i] ?? '').trim().toLowerCase(); if (v) counts.set(v, (counts.get(v) ?? 0) + 1) }
  return [...counts].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count, suggested: suggestKind(value) }))
}

// ---- value cleaners (client side; the server validates again and flags anything it cannot understand) -------------------
/** "1 234,5" / "1.234,5" / "1234.5" -> "1234.5". Returns the input unchanged if it does not look numeric (the server will flag it). */
export function cleanHours(v: string): string {
  const s = v.replace(/[\s  ]/g, '')
  if (!s) return ''
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return s.replace(/\./g, '').replace(',', '.')
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) return s.replace(/,/g, '')
  if (/^\d+,\d+$/.test(s)) return s.replace(',', '.')
  return /^\d+(\.\d+)?$/.test(s) ? s : v
}
/** dd/mm/yyyy, dd.mm.yyyy, dd-mm-yyyy (day first: this is a French club) and ISO -> yyyy-mm-dd; anything else is left for the server to flag. */
export function cleanDate(v: string): string {
  const s = v.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s)
  if (m) {
    const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])]
    const dt = new Date(Date.UTC(y, mo - 1, d))
    if (dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d) return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  }
  return v
}
/** A year column sometimes holds a full date: keep the year. */
export function cleanYear(v: string): string {
  const s = v.trim()
  const iso = /^(\d{4})-\d{2}-\d{2}$/.exec(s) ?? /^\d{1,2}[/.-]\d{1,2}[/.-](\d{4})$/.exec(s)
  return iso ? iso[1]! : s
}

// ---- tags: when the file has no code column --------------------------------------------------------------------------
/** PREFIX-001, PREFIX-002 ... skipping tags that already exist (case-insensitive) or were just generated. */
export function generateTags(count: number, prefix: string, existing: Iterable<string>, start = 1, width = 3): string[] {
  const taken = new Set([...existing].map((t) => t.toUpperCase()))
  const p = prefix.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '')
  const out: string[] = []
  for (let n = start; out.length < count; n++) {
    const tag = `${p}${String(n).padStart(width, '0')}`
    if (!taken.has(tag)) { taken.add(tag); out.push(tag) }
  }
  return out
}
